import { useEffect, useMemo, useState } from 'react';
import { useRoute } from '@react-navigation/native';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { getMachinesGroup, configureGroup, selectMachinesGroup, selectMachines } from '@reducers/machines';
import { getStateParams, navigateBack, navigateTo } from '@helpers';
import { scale, width } from '@components/frame';

export interface SelectMachineParams {
  machine: Record<string, any>;
  selected: boolean;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useManageMachinesGroupDetails = () => {
  const route = useRoute();
  const dispatch = useAppDispatch();
  const machinesGroup = useSelector(selectMachinesGroup) || [];
  const machines = useSelector(selectMachines);

  const id = getStateParams(route).id;

  const currentGroup = useMemo(
    () => machinesGroup.find((d: any) => d.machineGroupId === id),
    [machinesGroup, id]
  );

  const [group, setGroup] = useState<Record<string, any>>(currentGroup || {});
  const [showMorePopup, setShowMorePopup] = useState(false);
  const [showEditGroupName, setShowEditGroupName] = useState(false);

  const toggleShowMorePopup = () => setShowMorePopup((prev) => !prev);
  const toggleEditGroupName = () => setShowEditGroupName((prev) => !prev);

  useEffect(() => {
    if (currentGroup) {
      setGroup(currentGroup);
    }
  }, [currentGroup]);

  useEffect(() => {
    let cancelled = false;

    if (!group.machineGroupId) {
      (async () => {
        const data = await dispatch(getMachinesGroup()) as any[];
        if (cancelled) return;
        const found = data?.find((d: any) => d.machineGroupId == id);
        if (!found) {
          navigateBack();
          return;
        }
        setGroup(found);
      })();
    }

    return () => { cancelled = true; };
  }, []);

  const selectMachine = async ({ machine, selected, setSaving }: SelectMachineParams) => {
    setSaving(true);
    try {
      const result = await dispatch(configureGroup({
        name: group.name,
        machineGroupId: group.machineGroupId,
        action: 'store',
        machines: [
          {
            machineId: machine.machineId,
            action: selected ? 'remove' : 'store',
          },
        ],
      } as any)) as any;

      if (result?.successDetails) {
        const data = await dispatch(getMachinesGroup()) as any[];
        const updated = data?.find((d: any) => d.machineGroupId == id);
        if (updated) setGroup(updated);
      }
    } finally {
      setSaving(false);
    }
  };

  const onEditGroupName = async (data: Record<string, any>, onClose: () => void) => {
    const newData = { ...group, ...data, action: 'store' };
    await dispatch(configureGroup(newData as any, () => {
      setGroup(newData);
      onClose();
    }));
  };

  const removeGroup = async () => {
    await dispatch(configureGroup(
      { machineGroupId: group.machineGroupId, action: 'remove' } as any,
      () => {
        navigateTo('Settings', { screen: 'Machines', params: { screen: 'MachinesScreen' } });
      }
    ));
  };

  const elementWidth = useMemo(
    () => (width - scale(366) - scale(18) * 2 - scale(12) * 2) / 3,
    []
  );

  const selectedList = useMemo(
    () => group?.machines?.map((i: any) => i.machineId) || [],
    [group?.machines]
  );

  return {
    group,
    machines,
    showMorePopup,
    showEditGroupName,
    elementWidth,
    selectedList,
    setShowMorePopup,
    toggleShowMorePopup,
    toggleEditGroupName,
    selectMachine,
    onEditGroupName,
    removeGroup,
  };
};
